import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contex/AuthContext";
import {
  addTask, addNote, addExpense, addCourse, updateBudget
} from "../lib/db";
import { useCourses } from "../hooks/useStudyOS";
import ProfileScreen from "../screens/ProfileScreen";

const COURSE_COLORS = [
  "#7C6EF8","#2ECC8A","#F0A429","#F05D5D","#3BB8F0","#E87BDB","#F07C3B"
];

const CAT_META = {
  Food:            { icon: "🍛", color: "rgba(46,204,138,.12)"  },
  Transport:       { icon: "🚌", color: "rgba(240,164,41,.12)"  },
  "Data & airtime":{ icon: "📶", color: "rgba(124,110,248,.12)" },
  "Books & printing":{ icon: "📚", color: "rgba(59,184,240,.12)"},
  Entertainment:   { icon: "🎉", color: "rgba(232,123,219,.12)" },
  Miscellaneous:   { icon: "💸", color: "rgba(240,93,93,.12)"   },
};

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
const { profile, logout, user, theme, toggleTheme } = useAuth();
  const { courses } = useCourses();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [selectedColor, setSelectedColor] = useState(COURSE_COLORS[0]);
  const [error, setError] = useState("");

  const current = location.pathname.replace("/", "") || "home";

   const navItems = [
  { path: "/",         label: "Home",     icon: <i className="fi fi-rs-home" style={{fontSize:"20px"}}></i> },
  { path: "/academic", label: "Academic", icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7L12 12L22 7L12 2Z"/><path d="M2 17L12 22L22 17"/><path d="M2 12L12 17L22 12"/></svg> },
  { path: "/tasks",    label: "Tasks",    icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 12L11 14L15 10"/><path d="M9 7H15"/><path d="M9 17H13"/></svg> },
  { path: "/notes",    label: "Notes",    icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h5"/></svg> },
  { path: "/focus",    label: "Focus",    icon: <i className="fi fi-rr-alarm-clock" style={{fontSize:"20px"}}></i> },
  { path: "/finance",  label: "Finance",  icon: <i className="fi fi-rr-usd-circle" style={{fontSize:"20px"}}></i> },
];


  const fabScreens = ["tasks","notes","finance","academic"];
  const fabVisible = fabScreens.includes(current);
  const fabModalMap = {
    tasks: "task", notes: "note", finance: "expense", academic: "course"
  };

  const openModal = (type) => {
    setModal(type); setForm({}); setError(""); setSelectedColor(COURSE_COLORS[0]);
  };
  const closeModal = () => { setModal(null); setForm({}); setError(""); };

  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";

  const handleSubmit = async () => {
    try {
      if (modal === "task") {
        if (!form.name) { setError("Task name is required"); return; }
        await addTask({ name: form.name, courseId: form.courseId || null, priority: form.priority || "normal", due: form.due || null });
      } else if (modal === "note") {
        if (!form.title) { setError("Title is required"); return; }
        await addNote({ title: form.title, content: form.content || "", courseId: form.courseId || null });
      } else if (modal === "expense") {
        if (!form.name || !form.amount) { setError("Description and amount are required"); return; }
        const cat = form.category || "Miscellaneous";
        const meta = CAT_META[cat] || CAT_META["Miscellaneous"];
        await addExpense({ name: form.name, amount: parseFloat(form.amount), category: cat, icon: meta.icon, color: meta.color });
      } else if (modal === "course") {
        if (!form.name) { setError("Course name is required"); return; }
        await addCourse({ name: form.name, code: form.code || "", credits: form.credits || "", color: selectedColor });
      } else if (modal === "budget") {
        if (!form.total) { setError("Total budget is required"); return; }
        const categories = ["Food","Transport","Data & airtime","Books & printing","Entertainment","Miscellaneous"]
          .map(name => ({ name, amount: parseFloat(form[name] || 0) }))
          .filter(c => c.amount > 0);
        await updateBudget({ total: parseFloat(form.total), categories });
      }
      closeModal();
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
  };

  const courseOptions = courses.map(c =>
    <option key={c.id} value={c.id}>{c.name}</option>
  );
  const [showProfile, setShowProfile] = useState(false);

  const modalContent = () => {
    if (modal === "task") return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Task name *</label>
          <input style={styles.input} placeholder="e.g. Submit assignment" value={form.name||""} onChange={e=>setForm({...form,name:e.target.value})} />
        </div>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Course</label>
            <select style={styles.input} value={form.courseId||""} onChange={e=>setForm({...form,courseId:e.target.value})}>
              <option value="">General</option>{courseOptions}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Priority</label>
            <select style={styles.input} value={form.priority||"normal"} onChange={e=>setForm({...form,priority:e.target.value})}>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Due date</label>
          <select style={styles.input} value={form.due||""} onChange={e=>setForm({...form,due:e.target.value})}>
            <option value="">No due date</option>
            <option>Today</option>
            <option>Tomorrow</option>
            <option>This week</option>
            <option>Next week</option>
          </select>
        </div>
      </>
    );
    if (modal === "note") return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Title *</label>
          <input style={styles.input} placeholder="Note title" value={form.title||""} onChange={e=>setForm({...form,title:e.target.value})} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Content</label>
          <textarea style={{...styles.input,minHeight:"90px",resize:"vertical"}} placeholder="Write your note here..." value={form.content||""} onChange={e=>setForm({...form,content:e.target.value})} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Course</label>
          <select style={styles.input} value={form.courseId||""} onChange={e=>setForm({...form,courseId:e.target.value})}>
            <option value="">No course</option>{courseOptions}
          </select>
        </div>
      </>
    );
    if (modal === "expense") return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Description *</label>
          <input style={styles.input} placeholder="e.g. Canteen lunch" value={form.name||""} onChange={e=>setForm({...form,name:e.target.value})} />
        </div>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Amount (GHS) *</label>
            <input style={styles.input} type="number" placeholder="0" value={form.amount||""} onChange={e=>setForm({...form,amount:e.target.value})} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Category</label>
            <select style={styles.input} value={form.category||"Food"} onChange={e=>setForm({...form,category:e.target.value})}>
  {Object.keys(CAT_META).map(c => <option key={c} value={c}>{c}</option>)}
</select>
          </div>
        </div>
      </>
    );
    if (modal === "course") return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Course name *</label>
          <input style={styles.input} placeholder="e.g. Macroeconomics" value={form.name||""} onChange={e=>setForm({...form,name:e.target.value})} />
        </div>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Course code</label>
            <input style={styles.input} placeholder="e.g. ECON 301" value={form.code||""} onChange={e=>setForm({...form,code:e.target.value})} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Credits</label>
            <input style={styles.input} type="number" placeholder="3" value={form.credits||""} onChange={e=>setForm({...form,credits:e.target.value})} />
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Colour</label>
          <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
            {COURSE_COLORS.map(c => (
              <div key={c} onClick={()=>setSelectedColor(c)} style={{width:"28px",height:"28px",borderRadius:"50%",background:c,border:selectedColor===c?"3px solid #fff":"3px solid transparent",cursor:"pointer"}} />
            ))}
          </div>
        </div>
      </>
    );
    if (modal === "budget") return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Total monthly budget (GHS) *</label>
          <input style={styles.input} type="number" placeholder="e.g. 800" value={form.total||""} onChange={e=>setForm({...form,total:e.target.value})} />
        </div>
        <label style={{...styles.label,marginBottom:"10px",display:"block"}}>Allocate by category (GHS)</label>
        {Object.keys(CAT_META).map(cat => (
          <div key={cat} style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"10px"}}>
            <span style={{flex:1,fontSize:"13px",color:"#9998A0"}}>{cat}</span>
            <input style={{...styles.input,width:"100px",padding:"8px"}} type="number" placeholder="0" value={form[cat]||""} onChange={e=>setForm({...form,[cat]:e.target.value})} />
          </div>
        ))}
      </>
    );
  };

 return (
    <div className="app-shell">
      <div className="topbar">
        <div style={styles.logo}>Study<span style={{color:"#7C6EF8"}}>OS</span></div>
      <div style={styles.topRight}>
  <span style={styles.greetText}>{greeting}</span>
  <button className="theme-toggle" onClick={toggleTheme}>
  {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
</button>
 <div style={{...styles.avatar, cursor:"pointer"}} 
  onClick={() => setShowProfile(true)} 
  title="Edit profile">
  {profile?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "S"}
</div>
  
</div>
      </div>

      <div className="app-content">
        <Outlet context={{ openModal }} />
      </div>

      <nav className="bottom-nav">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <button key={item.path} className="nav-btn" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"4px",border:"none",background:"transparent",cursor:"pointer",padding:"6px 0",flex:1}} onClick={() => navigate(item.path)}>
             <span style={{color: isActive ? "#7C6EF8" : "#5C5B63", display:"flex", alignItems:"center", justifyContent:"center", transition:"color .15s"}}>
  {item.icon}
</span>
              <span className="nav-label" style={{fontSize:"10px",fontWeight:500,fontFamily:"inherit",color: isActive ? "#7C6EF8" : "#5C5B63"}}>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {fabVisible && (
        <button className="fab" onClick={() => openModal(fabModalMap[current] || "task")}>+</button>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box">
            <div style={styles.handle} />
            <div style={styles.modalTitle}>
              {modal === "task" ? "Add task" : modal === "note" ? "New note" : modal === "expense" ? "Log expense" : modal === "course" ? "Add course" : "Set budget"}
            </div>
            {modalContent()}
            {error && <div style={styles.errorText}>{error}</div>}
            <button style={styles.submitBtn} onClick={handleSubmit}>Save</button>
          </div>
        </div>
      )}
      {showProfile && <ProfileScreen onClose={() => setShowProfile(false)} />}
    </div>
  );
}

const isMobile = window.innerWidth <= 768;

const styles = {
  shell:      { display:"flex", flexDirection:"column", minHeight:"100vh", maxWidth: isMobile ? "480px" : "100%", margin:"0 auto", background:"#0F0F13" },
  topbar:     { position:"sticky", top:0, zIndex:50, background:"rgba(15,15,19,0.95)", backdropFilter:"blur(20px)", borderBottom:"1px solid rgba(255,255,255,0.07)", padding:"0 20px", height:"56px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  logo:       { fontSize:"17px", fontWeight:700, color:"#F0EFE8", letterSpacing:"-0.5px" },
  topRight:   { display:"flex", alignItems:"center", gap:"12px" },
  greetText:  { fontSize:"13px", color:"#9998A0" },
  avatar:     { width:"32px", height:"32px", borderRadius:"50%", background:"#7C6EF8", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:600, color:"#fff" },
  content:    { flex:1, paddingBottom:"80px", maxWidth: isMobile ? "100%" : "900px", margin:"0 auto", width:"100%" },
  bottomNav:  { position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:"100%", background:"rgba(24,24,31,0.97)", backdropFilter:"blur(20px)", borderTop:"1px solid rgba(255,255,255,0.07)", display:"flex", padding:"8px 0 16px", zIndex:50 },
  navBtn:     { flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", border:"none", background:"transparent", cursor:"pointer", padding:"6px 0" },
  navLabel:   { fontSize:"10px", fontWeight:500, fontFamily:"inherit" },
  fab:        { position:"fixed", bottom:"80px", right: isMobile ? "calc(50% - 220px)" : "40px", width:"54px", height:"54px", borderRadius:"50%", background:"#7C6EF8", border:"none", color:"#fff", fontSize:"26px", cursor:"pointer", boxShadow:"0 4px 20px rgba(124,110,248,.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:49 },
  overlay:    { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:200 },
  modalBox:   { background:"#18181F", borderRadius:"24px 24px 0 0", padding:"20px 20px 40px", width:"100%", maxWidth: isMobile ? "480px" : "600px", maxHeight:"90vh", overflowY:"auto" },
  handle:     { width:"38px", height:"4px", background:"#2A2A36", borderRadius:"2px", margin:"0 auto 18px" },
  modalTitle: { fontSize:"18px", fontWeight:700, color:"#F0EFE8", marginBottom:"18px" },
  formGroup:  { marginBottom:"14px" },
  formRow:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" },
  label:      { fontSize:"12px", fontWeight:600, color:"#9998A0", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"6px", display:"block" },
  input:      { width:"100%", padding:"12px 14px", background:"#0F0F13", border:"1px solid rgba(255,255,255,0.13)", borderRadius:"8px", fontFamily:"inherit", fontSize:"14px", color:"#F0EFE8", outline:"none" },
  errorText:  { fontSize:"13px", color:"#F05D5D", marginBottom:"10px" },
  submitBtn:  { width:"100%", padding:"14px", background:"#7C6EF8", border:"none", borderRadius:"12px", fontFamily:"inherit", fontSize:"15px", fontWeight:600, color:"#fff", cursor:"pointer", marginTop:"4px" },
};